import { CoreStore } from '../../../../core/store/core.store';
import { TwoFactorToastExpiredTokenComponent } from '../components/two-factor-toast-expired-token/two-factor-toast-expired-token.component';
import { hasSecurityTokenExpired, TwoFAType } from '../domain/two-factor';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { getLoggedInUser, PplDateTimePipe } from '@ppl/auth';
import { I18nService } from '@ppl/i18n';
import { ToastService } from '@ppl/ui/toast';
import { pairs } from '@ppl/utils/array.utils';
import { downloadFile } from '@ppl/utils/file.utils';
import { defaultRetryStrategy, pollWhile } from '@ppl/utils/polling.utils';
import { assertNever } from '@ppl/utils/typescript.utils';
import moment from 'moment';
import {
  EMPTY,
  pipe,
  Subject,
  throwError
} from 'rxjs';
import { catchError, map, retry } from 'rxjs/operators';
import type { HttpErrorResponse } from '@angular/common/http';
import type { ViewContainerRef } from '@angular/core';
import type { Toast } from '@ppl/ui/toast';
import type {
  MonoTypeOperatorFunction,
  Observable
} from 'rxjs';
import type { Translations } from '../../../../../translations';
import type { TwoFactorToastExpiredTokenData } from '../components/two-factor-toast-expired-token/two-factor-toast-expired-token.component';
import type {
  TwoFactorSecurityTokenData,
  TwoFAMethod,
  TwoFAMethodValue
} from '../domain/two-factor';


@Injectable({
  providedIn: 'root'
})
export class TwoFactorService {

  private securityTokenExpiry$ = new Subject<{ securityToken: string }>();
  private expiredTokenToasts: { [key: string]: Toast<TwoFactorToastExpiredTokenData> } = {};

  get securityTokenExpired$() {
    return this.securityTokenExpiry$.asObservable();
  }

  constructor(
    private store: CoreStore,
    private http: HttpClient,
    private i18nService: I18nService<typeof Translations>,
    private toastService: ToastService,
    private dateTimePipe: PplDateTimePipe
  ) { }

  getMethodName(type: TwoFAType) {
    switch (type) {
      case TwoFAType.DUMMY:
        return this.i18nService.translate('Dummy');
      case TwoFAType.SMS:
        return this.i18nService.translate('Text_Message_(SMS)');
      case TwoFAType.EMAIL:
        return this.i18nService.translate('Email_Message_Confirmation');
      case TwoFAType.AUTH_APP:
        return this.i18nService.translate('Authenticator_App');
      case TwoFAType.BACKUP_CODES:
        return this.i18nService.translate('Backup_Codes');
      case TwoFAType.EMAIL_MS_IDENTITY_CONFIRMATION:
        return this.i18nService.translate('Microsoft_Identity_Confirmation');
      default:
        assertNever(type);
    }
  }

  showExpiredTokenToast(
    toastId: string,
    data: TwoFactorToastExpiredTokenData,
    options: { viewContainerRef?: ViewContainerRef } = {}
  ) {
    const previousToastWithSameId = this.expiredTokenToasts[toastId];

    if (previousToastWithSameId) {
      delete this.showExpiredTokenToast[toastId];
      this.toastService.hide(previousToastWithSameId);
    }

    const toast: Toast<TwoFactorToastExpiredTokenData> = {
      component: TwoFactorToastExpiredTokenComponent,
      viewContainerRef: options.viewContainerRef,
      data: data,
      options: {
        sticky: true
      }
    };
    this.expiredTokenToasts[toastId] = toast;
    this.toastService.show<TwoFactorToastExpiredTokenData>(toast);
  }

  needSecurityTokenRefresh(data: TwoFactorSecurityTokenData | null): boolean {
    return !data?.token || hasSecurityTokenExpired(data);
  }

  getSecurityToken(data: { username: string, password?: string }): Observable<TwoFASecurityTokenResponse> {
    return this.http.post<TwoFASecurityTokenResponse>(
      `/rest/2fa/unlock`,
      data,
      {
        responseType: 'json',
        withCredentials: true,
      }
    );
  }

  fetchMethods(): Observable<TwoFAMethod[]> {
    return this.http.get<{ methods: TwoFAMethod[] }>(
      `/rest/2fa/methods`,
      {
        responseType: 'json',
        withCredentials: true,
      }
    ).pipe(
      map(response => {
        return response.methods;
      })
    );
  }

  getMethodValue(data: { id: number, securityToken: string }): Observable<TwoFAMethodDataResponse> {
    return this.securityGet<TwoFAMethodDataResponse>(
      `/rest/2fa/${data.id}/data`,
      data.securityToken
    );
  }

  disable2StepVerification(data: { securityToken: string }): Observable<null> {
    return this.securityPost('/rest/2fa/disable', data.securityToken).pipe(
      map(() => {
        return null;
      })
    );
  }

  toggleMethod(data: { id: number, enable: boolean, securityToken: string }): Observable<null> {
    return this.securityPost(
      `/rest/2fa/${data.id}/${data.enable ? 'enable' : 'disable'}`,
      data.securityToken
    ).pipe(
      map(() => null)
    );
  }

  deleteMethod(data: { id: number, securityToken: string }): Observable<null> {
    return this.securityPost(
      `/rest/2fa/${data.id}/delete`,
      data.securityToken
    ).pipe(
      map(() => null)
    );
  }

  toggleBackupCodes(
    data: { method: TwoFAMethod | null, enable: boolean, securityToken: string }
  ): Observable<{ result: TwoFAResult }> {
    const operation: 'delete' | 'setup' | 'none' = data.enable
      ? 'setup'
      : data.method
        ? 'delete'
        : 'none';

    switch (operation) {
      case 'delete':
        return this.deleteMethod({
          id: data.method.id,
          securityToken: data.securityToken
        }).pipe(
          map(() => {
            return {
              result: TwoFAResult.FINISHED
            };
          })
        );
      case 'setup':
        return this.setupStart({
          type: TwoFAType.BACKUP_CODES,
          securityToken: data.securityToken
        }).pipe(
          map(response => {
            return {
              result: response.result
            };
          })
        );
      case 'none':
        return EMPTY;
      default:
        assertNever(operation);
    }
  }

  verifyStart(data: { jwt: string, methodId: number }): Observable<null> {
    return this.http.post(
      `/rest/2fa/${data.methodId}/start`,
      { jwt: data.jwt },
      { responseType: 'json' }
    ).pipe(
      map(() => {
        return null;
      })
    );
  }

  verifyChallenge(data: {
    jwt: string,
    methodId: number,
    code: string,
    remember?: boolean
  }): Observable<TwoFAChallengeResponse> {
    return this.http.post<TwoFAChallengeResponse>(
      `/rest/2fa/${data.methodId}/challenge`,
      {
        jwt: data.jwt,
        value: data.code,
        remember: data.remember
      },
      { responseType: 'json' }
    );
  }

  verifyChallengeStatus(data: {
    jwt: string,
    methodId: number
  }): Observable<TwoFAChallengeResponse> {
    return this.http.get<TwoFAChallengeResponse>(
      `/rest/2fa/${data.methodId}/challenge/status`, {
      params: { jwt: data.jwt },
      responseType: 'json'
    });
  }

  pollStatus<T extends { result: TwoFAResult }>(
    observable$: Observable<T>,
    options: { pollPeriod: number } = { pollPeriod: 3000 }
  ) {
    return pollWhile(
      observable$,
      response => {
        return response.result !== TwoFAResult.IN_PROGRESS;
      },
      options
    ).pipe(
      retry(defaultRetryStrategy())
    );
  }

  setupStart(data: {
    type: TwoFactorSetupType,
    securityToken: string,
    value?: string,
    name?: string // used for auth_app method
  }): Observable<TwoFASetupStartResponse> {
    return this.securityPost<TwoFASetupStartResponse>(
      `/rest/2fa/${data.type}/setup`,
      data.securityToken,
      {
        ...(data.value ? { value: data.value } : {}),
        ...(data.name ? { display_name: data.name } : {})
      }
    );
  }

  setupVerify(data: {
    type: TwoFactorSetupType,
    setupToken: string,
    code: string | null
  }): Observable<TwoFASetupVerifyResponse> {
    return this.http.post<TwoFASetupVerifyResponse>(
      `/rest/2fa/${data.type}/setup/finish`,
      {
        setup_token: data.setupToken,
        value: data.code
      },
      { responseType: 'json' }
    );
  }

  setupVerifyStatus(data: {
    type: TwoFactorSetupType,
    setupToken: string
  }): Observable<TwoFASetupVerifyResponse> {
    return this.http.get<TwoFASetupVerifyResponse>(
      `/rest/2fa/${data.type}/setup/status`, {
      params: { setup_token: data.setupToken },
      responseType: 'json'
    });
  }

  getErrorInfo(error: HttpErrorResponse): TwoFactorErrorInfo {
    switch (error.status) {
      case 429:
        return {
          error: TwoFactorError.TOO_MANY_REQUESTS,
          message: this.i18nService.translate('Temporarily_blocked_because_of_too_many_attempts.'),
          retryAfter: this.getRetryAfter(error)
        };
      case 403:
        return {
          error: TwoFactorError.INVALID_OR_EXPIRED_TOKEN,
          message: this.i18nService.translate('Invalid_or_expired_token.')
        };
      case 401:
        return {
          error: TwoFactorError.UNAUTHORIZED,
          message: this.i18nService.translate('Unauthorized')
        };
      case 404:
        return {
          error: TwoFactorError.NOT_FOUND,
          message: this.i18nService.translate('Not_found.')
        };
      case 0:
        return {
          error: TwoFactorError.CLIENT_OR_NETWORK_ERROR,
          message: this.i18nService.translate('Client_or_network_error.')
        };
      default:
        return {
          error: TwoFactorError.UNKNOWN,
          message: this.i18nService.translate('Unknown_error.')
        };
    }
  }

  createBackupCodesFileContent(backupCodes: (string | null)[]) {
    const user = this.store.get(getLoggedInUser);
    const backupCodesInPairs = pairs(backupCodes, 2);
    const emptyCodeStr = '... ...';
    const content = [
      this.i18nService.translate('Backup_Codes').toUpperCase(),
      `(${user.profile.email})`,
      '',
      ...backupCodesInPairs.map(([first, second], index) => {
        return `${index + (index + 1)}. ${first || emptyCodeStr}\t${index + (index + 2)}. ${second || emptyCodeStr}`;
      }),
      '',
      this.i18nService.translate('Keep_these_backup_codes_somewhere_safe_but_accessible.')
    ].join('\n');

    return content;
  }

  downloadBackupCodes(backupCodes: (string | null)[]) {
    const content = this.createBackupCodesFileContent(backupCodes);
    const file = new Blob([content], { type: 'text/plain;encoding:utf-8' });
    const fileURL = URL.createObjectURL(file);
    downloadFile(fileURL, { suggestedFileName: 'backup-codes.txt' });
  }

  handleError<T>(options: {
    errorHandlerFn: (error: TwoFactorErrorInfo, originalError: HttpErrorResponse) => Observable<never>
  }): MonoTypeOperatorFunction<T> {
    return pipe(
      catchError((error: HttpErrorResponse) => {
        const errorInfo = this.getErrorInfo(error);

        if (options.errorHandlerFn) {
          return options.errorHandlerFn(errorInfo, error);
        } else {
          return throwError(error);
        }
      })
    );
  }

  getRetryAfterMessage(retryAfter: string) {
    const errorMessages = [this.i18nService.translate('Too_many_requests.')];

    if (retryAfter) {
      errorMessages.push(
        this.i18nService.translate(
          'Try_again_after_%dateTime%',
          { dateTime: this.dateTimePipe.transform(retryAfter) }
        )
      );
    }

    return errorMessages.join(' ');
  }

  private getRetryAfter(error: HttpErrorResponse): string | null {
    if (error.headers.has('Retry-After')) {
      return error.headers.get('Retry-After');
    } else if (error.headers.has('X-RateLimit-WaitFor')) {
      const secondsToWait = error.headers.get('X-RateLimit-WaitFor');
      return moment.utc().add(secondsToWait, 'seconds').format();
    } else {
      return null;
    }
  }

  private handleTokenExpiry(token: string, callbackFn: (token: string) => void) {
    return (error: HttpErrorResponse) => {
      const errorInfo = this.getErrorInfo(error);
      if (errorInfo.error === TwoFactorError.INVALID_OR_EXPIRED_TOKEN) {
        callbackFn(token);
      }
      return throwError(error);
    };
  }

  private handleSecurityTokenExpiry(securityToken: string) {
    return this.handleTokenExpiry(securityToken, () => {
      this.securityTokenExpiry$.next({ securityToken });
    });
  }

  private securityGet<T>(
    url: string,
    securityToken: string
  ): Observable<T> {
    return this.http.get<T>(
      url, {
      responseType: 'json',
      params: {
        security_token: securityToken
      }
    }).pipe(
      catchError(this.handleSecurityTokenExpiry(securityToken))
    );
  }

  private securityPost<T>(
    url: string,
    securityToken: string,
    body?: object | null
  ) {
    return this.http.post<T>(
      url,
      {
        ...(body ? body : {}),
        security_token: securityToken
      },
      { responseType: 'json' }
    ).pipe(
      catchError(this.handleSecurityTokenExpiry(securityToken))
    );
  }
}

export enum TwoFAResult {
  IN_PROGRESS = 0,
  FINISHED = 1,
  FAILED = 2,
  EXPIRED = 3
}


export enum TwoFactorError {
  UNKNOWN = 1,
  CLIENT_OR_NETWORK_ERROR,
  TOO_MANY_REQUESTS,
  INVALID_OR_EXPIRED_TOKEN,
  UNAUTHORIZED,
  NOT_FOUND
}

export interface TwoFactorErrorInfo {
  error: TwoFactorError;
  message: string;
  retryAfter?: string;
}

export type TwoFactorSetupType = TwoFAType.EMAIL
  | TwoFAType.SMS
  | TwoFAType.AUTH_APP
  | TwoFAType.BACKUP_CODES;

export interface TwoFAChallengeResponse {
  result: TwoFAResult;
  redirect_url: string | null;
  pipeliner_token?: string;
  ppl_refresh_token?: string;
}

export interface TwoFASecurityTokenResponse {
  security_token: string;
  redirect_url: string;
  // NOTE(mike): datetime string in utc format
  valid_until: string;
}

export interface TwoFASetupStartResponse {
  result: TwoFAResult;
  setup_token: string;
  key: string | null;
}

export interface TwoFASetupVerifyResponse {
  result: TwoFAResult;
  id: number | null;
}

export interface TwoFAMethodDataResponse {
  id: number;
  data: TwoFAMethodValue[];
}
